import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  uploadDocumentFiles, UploadResponse,
  createKnowledgeUnit, KnowledgeUnitResult,
  getDocuments, Document,
} from '../api/documents';
import { getDomains, Domain } from '../api/data';
import PageContainer from '../components/PageContainer';

/**
 * Knowledge-unit upload — the admin panel now uses the SAME one-file-one-chunk
 * ingestion pipeline as the bulk corpus ingest. Two kinds of .md are accepted:
 *   • Knowledge units: YAML frontmatter (language, title, document_slug,
 *     knowledge_unit_slug, source_document, generated_questions…) + Markdown
 *     body. Each file becomes exactly ONE retrievable chunk, upserted by its
 *     stable id (re-uploading a corrected unit replaces just that chunk).
 *   • Source pages: plain .md WITHOUT frontmatter, whose FILENAME equals the
 *     units' `source_document` value — shown to users as the citation page,
 *     never embedded.
 * The legacy paste-text / auto-chunking flow is retired.
 */

interface StagedFile {
  file: File;
  kind: 'unit' | 'source_page';
}

/* Downloadable starter template for the advanced .md flow. Every value is a
   placeholder that parses cleanly, and each field carries a YAML comment
   explaining what to write — edit, save, upload. */
const UNIT_TEMPLATE = `---
# ═══ Knowledge unit template ═══════════════════════════════════════════
# One file = ONE answer to ONE question. Fill every field below, save the
# file with any name ending in .md, then upload it on this page.
# Lines starting with # are comments — you can delete them.

# "ar" or "en". Make an English twin by copying this file with language: "en",
# translated title/body/questions, and the SAME slugs below.
language: "ar"

# The entry's title — what this unit answers (in the unit's language).
title: "عنوان الوحدة — ماذا تجيب هذه الوحدة؟"

# Which document this belongs to. Units sharing the same value are grouped
# under ONE document, and citations open that document's source page.
# Must end in .md — e.g. "دليل الخدمات الطلابية.md"
source_document: "اسم-الوثيقة.md"

# Two PERMANENT ids in English letters (a-z, 0-9, hyphens). Together they
# identify this unit forever: re-uploading a file with the same two slugs
# UPDATES the unit instead of duplicating it. Pick short descriptive names.
document_slug: "my-document"
knowledge_unit_slug: "my-topic"

# Optional but important: 3-5 questions phrased the way real students ask
# (copy phrasings from the Content Gaps page). They power retrieval AND the
# "you might also ask" chips.
generated_questions:
  - "سؤال كما يكتبه الطالب فعلاً؟"
  - "صياغة ثانية لنفس السؤال؟"
  - "صياغة ثالثة؟"

# Optional fields ─────────────────────────────────────────────────────────
content_type: "reference"        # reference | procedure | policy | faq
section_path: ["القسم", "الموضوع"]  # breadcrumb shown with citations
tags: ["وسم-أول", "وسم-ثانٍ"]
confidence: 0.95                 # how sure you are of this content (0-1)
---

# عنوان الوحدة — ماذا تجيب هذه الوحدة؟

اكتب الإجابة الكاملة هنا بصيغة Markdown. هذا النص هو ما سيقرأه الطالب
حرفياً — بالأرقام الدقيقة والشروط وجهات التواصل، دون أي عبارات مؤقتة.

- النقاط والقوائم مدعومة
- والجداول أيضاً:

| البند | القيمة |
|-------|--------|
| مثال  | 123    |
`;

function downloadUnitTemplate() {
  const blob = new Blob([UNIT_TEMPLATE], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'knowledge-unit-template.md';
  a.click();
  URL.revokeObjectURL(url);
}

const DocumentCreate: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [staged, setStaged] = useState<StagedFile[]>([]);
  const [uploadDomainId, setUploadDomainId] = useState('');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<UploadResponse | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [tab, setTab] = useState<'form' | 'files'>('form');
  const [docTitles, setDocTitles] = useState<string[]>([]);

  useEffect(() => {
    getDomains(1, 100).then(res => setDomains(res.items)).catch(() => {});
    getDocuments().then((docs: Document[]) => setDocTitles(docs.map(d => d.title))).catch(() => {});
  }, []);

  // Client-side classification mirrors the server: frontmatter fence = unit.
  const stageFiles = async (files: File[]) => {
    const mdFiles = files.filter(f => f.name.endsWith('.md'));
    const classified = await Promise.all(mdFiles.map(async (file) => {
      const head = (await file.slice(0, 16).text()).replace(/^﻿/, '').trimStart();
      return { file, kind: head.startsWith('---') ? 'unit' : 'source_page' } as StagedFile;
    }));
    setStaged(prev => [...prev, ...classified]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    stageFiles(Array.from(e.dataTransfer.files));
  };

  const removeFile = (idx: number) => setStaged(prev => prev.filter((_, i) => i !== idx));

  const handleUpload = async () => {
    if (staged.length === 0) { alert('Please select knowledge-unit files first'); return; }
    setLoading(true);
    setResponse(null);
    try {
      const result = await uploadDocumentFiles(
        staged.map(s => s.file),
        uploadDomainId ? parseInt(uploadDomainId) : undefined,
      );
      setResponse(result);
      setStaged([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error: any) {
      alert('Upload failed: ' + (error.response?.data?.detail || error.message));
    } finally { setLoading(false); }
  };

  const unitCount = staged.filter(s => s.kind === 'unit').length;
  const pageCount = staged.length - unitCount;

  return (
    <PageContainer
      title="Add Knowledge"
      subtitle="Each entry answers one question — it becomes exactly one retrievable chunk, live immediately."
      actions={
        <Link to="/documents" className="btn-ghost text-xs gap-1">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          Back to List
        </Link>
      }
    >
      {/* Mode tabs */}
      <div className="border-b border-primary-200 flex items-center gap-6 -mt-2">
        {([['form', 'Write a knowledge unit'], ['files', 'Upload .md files (advanced)']] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`pb-2.5 -mb-px text-sm font-medium border-b-2 transition-colors ${
              tab === key ? 'text-primary-900 border-primary' : 'text-primary-500 border-transparent hover:text-primary-800'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'form' && (
        <UnitForm domains={domains} docTitles={docTitles} onCreatedDoc={(t) => setDocTitles(prev => prev.includes(t) ? prev : [...prev, t])} />
      )}

      {tab === 'files' && (
      <div className="card p-6 space-y-5">
        {/* Format explainer */}
        <div className="rounded-xl bg-primary-50 border border-primary-200 px-4 py-3 text-xs text-primary-600 leading-relaxed">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-semibold text-primary-700 mb-1">Accepted files (.md)</p>
              <p><span className="font-medium text-primary-700">Knowledge units</span> — YAML frontmatter
                (<code>language</code>, <code>title</code>, <code>document_slug</code>, <code>knowledge_unit_slug</code>,{' '}
                <code>source_document</code>, <code>generated_questions</code>…) + Markdown body.
                Each file becomes exactly <span className="font-medium">one chunk</span>; re-uploading the same unit updates it in place.</p>
              <p className="mt-1"><span className="font-medium text-primary-700">Source pages</span> — plain .md named exactly like the
                units' <code>source_document</code>. Shown as the citation page, never embedded.</p>
            </div>
            <button type="button" onClick={downloadUnitTemplate} className="btn-secondary text-xs gap-1.5 flex-shrink-0">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Download template
            </button>
          </div>
        </div>

        {/* Drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${dragOver ? 'border-primary bg-primary/5' : 'border-primary-300 hover:border-primary-400'}`}
        >
          <svg className="w-10 h-10 mx-auto text-primary-400 mb-3" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
          </svg>
          <p className="text-sm font-medium text-primary-700">Drop knowledge-unit .md files here, or click to browse</p>
          <p className="text-xs text-primary-400 mt-1">Files with frontmatter are units; plain .md files are source pages</p>
          <input ref={fileInputRef} type="file" multiple accept=".md" className="hidden" onChange={(e) => {
            stageFiles(Array.from(e.target.files || []));
          }} />
        </div>

        {staged.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-primary-500 uppercase tracking-wide">
              {unitCount} unit{unitCount === 1 ? '' : 's'}{pageCount > 0 && `, ${pageCount} source page${pageCount === 1 ? '' : 's'}`} selected
            </p>
            {staged.map((s, idx) => (
              <div key={idx} className="flex items-center justify-between px-3 py-2 bg-primary-50 rounded-lg border border-primary-200">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${s.kind === 'unit' ? 'bg-primary-50 text-primary-700' : 'bg-amber-50 text-amber-700'}`}>
                    {s.kind === 'unit' ? 'unit' : 'source page'}
                  </span>
                  <span className="text-sm text-primary-700 truncate">{s.file.name}</span>
                  <span className="text-xs text-primary-400">{(s.file.size / 1024).toFixed(1)} KB</span>
                </div>
                <button onClick={() => removeFile(idx)} className="text-primary-400 hover:text-red-500 transition-colors">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label">Domain (optional)</label>
            <select value={uploadDomainId} onChange={(e) => setUploadDomainId(e.target.value)} className="input-base">
              <option value="">General (no domain)</option>
              {domains.map(d => <option key={d.id} value={d.id}>{d.name_en}</option>)}
            </select>
          </div>
        </div>

        <button onClick={handleUpload} disabled={loading || staged.length === 0} className="btn-primary gap-1.5">
          {loading ? (
            <><svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg> Ingesting…</>
          ) : (
            <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" /></svg> Upload & Ingest</>
          )}
        </button>

        {response && (
          <div className="space-y-2 pt-3 border-t border-primary-200">
            <p className="text-sm font-semibold text-primary-800">
              Results — {response.units_created} created, {response.units_updated} updated
              {response.documents_created > 0 && `, ${response.documents_created} new document${response.documents_created === 1 ? '' : 's'}`}
            </p>
            {response.files.filter(f => f.status === 'error' || f.status === 'skipped').map((f, idx) => (
              <div key={`e${idx}`} className="px-3 py-2 rounded-lg text-sm bg-red-50 text-red-800">
                <span className="font-medium">{f.filename}</span> — {f.error}
              </div>
            ))}
            {response.results.map((r, idx) => (
              <div key={idx} className={`px-3 py-2 rounded-lg text-sm ${r.action === 'error' ? 'bg-red-50 text-red-800' : r.action === 'updated' ? 'bg-amber-50 text-amber-800' : 'bg-emerald-50 text-emerald-800'}`}>
                <span className="font-mono text-xs">{r.stable_id || r.document}</span>
                <span> — {r.action}{r.document && r.stable_id ? ` (${r.document})` : ''}{r.error ? `: ${r.error}` : ''}</span>
              </div>
            ))}
            <Link to="/documents" className="btn-secondary text-xs inline-flex mt-2">View Documents</Link>
          </div>
        )}
      </div>
      )}
    </PageContainer>
  );
};

/* Simple form — no .md files, no YAML, no slugs. Server generates everything
   and ingests through the same one-unit-one-chunk pipeline. */
const UnitForm: React.FC<{
  domains: Domain[];
  docTitles: string[];
  onCreatedDoc: (title: string) => void;
}> = ({ domains, docTitles, onCreatedDoc }) => {
  const [language, setLanguage] = useState<'ar' | 'en'>('ar');
  const [domainId, setDomainId] = useState('');
  const [documentTitle, setDocumentTitle] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [questionsText, setQuestionsText] = useState('');
  const [tagsText, setTagsText] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<KnowledgeUnitResult | null>(null);

  const questions = questionsText.split('\n').map(q => q.trim()).filter(Boolean);
  const ready = documentTitle.trim().length >= 2 && title.trim().length >= 2
    && body.trim().length >= 10 && questions.length >= 1;

  const handleSave = async () => {
    if (!ready || saving) return;
    setSaving(true);
    setResult(null);
    try {
      const res = await createKnowledgeUnit({
        language,
        document_title: documentTitle.trim(),
        title: title.trim(),
        body: body.trim(),
        questions,
        tags: tagsText.split(',').map(t => t.trim()).filter(Boolean),
        domain_id: domainId ? parseInt(domainId) : undefined,
      });
      setResult(res);
      onCreatedDoc(documentTitle.trim());
      // Keep document/domain/language for the next unit; clear the unit itself.
      setTitle(''); setBody(''); setQuestionsText(''); setTagsText('');
    } catch (error: any) {
      alert('Save failed: ' + (error.response?.data?.detail || error.message));
    } finally { setSaving(false); }
  };

  return (
    <div className="card p-6 space-y-5">
      <div className="rounded-xl bg-primary-50/60 border border-primary-100 px-4 py-3 text-sm text-primary-600 leading-relaxed">
        Write <span className="font-semibold text-primary-800">one answer to one question</span> — e.g. "student housing
        options" or "documents required for Bagrut applicants". The chatbot will quote the answer text as-is.
        Saving again with the same titles updates the existing entry instead of duplicating it.
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="label">Language</label>
          <div className="inline-flex items-center bg-primary-100 rounded-lg p-0.5 w-full">
            {(['ar', 'en'] as const).map(l => (
              <button key={l} type="button" onClick={() => setLanguage(l)}
                className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  language === l ? 'bg-white text-primary-900 shadow-card' : 'text-primary-500 hover:text-primary-800'
                }`}>
                {l === 'ar' ? 'العربية' : 'English'}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="label">Audience</label>
          <select value={domainId} onChange={(e) => setDomainId(e.target.value)} className="input-base">
            <option value="">Everyone (General)</option>
            {domains.map(d => <option key={d.id} value={d.id}>{d.name_en} only</option>)}
          </select>
        </div>
        <div>
          <label className="label">Document (topic group)</label>
          <input
            type="text" list="docTitleOptions" value={documentTitle}
            onChange={(e) => setDocumentTitle(e.target.value)}
            className="input-base" dir="auto"
            placeholder='e.g. دليل الخدمات الطلابية'
          />
          <datalist id="docTitleOptions">
            {docTitles.map(t => <option key={t} value={t} />)}
          </datalist>
          <p className="text-xs text-primary-400 mt-1">Pick an existing document or type a new name — it's created automatically.</p>
        </div>
      </div>

      <div>
        <label className="label">Entry title — what this answers</label>
        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
          className="input-base" dir="auto"
          placeholder={language === 'ar' ? 'مثال: السكن الطلابي القريب من الجامعة' : 'e.g. Student housing near campus'} />
      </div>

      <div>
        <label className="label">The answer</label>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8}
          className="input-base leading-relaxed" dir="auto"
          placeholder={language === 'ar'
            ? 'اكتب الإجابة الكاملة هنا — بالأرقام والشروط وجهات التواصل كما يجب أن يقرأها الطالب حرفياً.'
            : 'Write the full answer here — with the exact numbers, conditions, and contacts the student should read.'} />
        <p className="text-xs text-primary-400 mt-1">This text is what the chatbot will say. No placeholders — exact numbers and names only.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">How would people ask this? (one per line)</label>
          <textarea value={questionsText} onChange={(e) => setQuestionsText(e.target.value)} rows={4}
            className="input-base" dir="auto"
            placeholder={language === 'ar'
              ? 'هل يوجد سكن قريب من الجامعة؟\nكيف أجد سكناً طلابياً؟'
              : 'Is there housing near campus?\nHow do I find student housing?'} />
          <p className="text-xs text-primary-400 mt-1">3–5 real phrasings — copy them from the Content Gaps page.</p>
        </div>
        <div>
          <label className="label">Tags (optional, comma-separated)</label>
          <input type="text" value={tagsText} onChange={(e) => setTagsText(e.target.value)}
            className="input-base" dir="auto" placeholder={language === 'ar' ? 'سكن, طلبة, خدمات' : 'housing, students, services'} />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button onClick={handleSave} disabled={!ready || saving} className="btn-primary gap-1.5">
          {saving ? (
            <><svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg> Saving…</>
          ) : 'Save & publish'}
        </button>
        {!ready && <span className="text-xs text-primary-400">Fill the document, title, answer, and at least one question.</span>}
      </div>

      {result && (
        <div className="px-4 py-3 rounded-lg bg-emerald-50 border border-emerald-100 text-sm text-emerald-800">
          <span className="font-semibold">{result.action === 'created' ? 'Published' : 'Updated'}</span> in
          "{result.document}" — the chatbot can use it now.
          <Link to="/retrieval-test" className="ms-2 underline underline-offset-2 font-medium">Try it in Preview answers</Link>
        </div>
      )}
    </div>
  );
};

export default DocumentCreate;
